import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, PackagePlus, CheckCircle2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { productsAPI, cartAPI, promosAPI } from '../services/api';
import { vehicleStore } from '../services/vehicleStore';
import { promoSession, PENDING_PROMO_KEY } from '../services/savings';
import { useT } from '../services/i18n';
import { prodName } from '../services/catalogI18n';
import { vehicleEnergy } from '../services/fuel';

const eur = (n) => `${Number(n || 0).toFixed(2)} €`;

// Engine-oil pages: « Pack entretien essence » = this oil + the best petrol fuel additive, discount applied through the PACK-ESSENCE code
export const PackEssenceCard = ({ product, vehicle, onAdded }) => {
  const { t, lang } = useT();
  const [pack, setPack] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const energy = vehicleEnergy(vehicle?.engine?.fuel);
  useEffect(() => {
    setPack(null);
    if (product?.subcategory !== 'Huile moteur' || (vehicle && !['essence', 'diesel'].includes(energy))) return;
    productsAPI.packEssence(product.id, vehicle ? vehicleStore.toParams(vehicle) : {}).then(setPack).catch(() => setPack(null));
  }, [product?.id, vehicle?.brand, vehicle?.model, vehicle?.engine?.name]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!pack?.available) return null;
  const add = async () => {
    setBusy(true);
    try {
      await cartAPI.addItem(product.id, 1);
      await cartAPI.addItem(pack.additive.id, 1);
      if (localStorage.getItem('token')) {
        const res = await promosAPI.validate(pack.code);
        promoSession.set(res);
        toast.success(t('packEss.added', { c: pack.code }));
      } else {
        sessionStorage.setItem(PENDING_PROMO_KEY, pack.code);
        toast.success(t('packEss.addedLogin', { c: pack.code }));
      }
      setDone(true);
      onAdded?.();
    } catch (e) {
      // toast already shown by cartAPI
    } finally {
      setBusy(false);
    }
  };
  return (
    <section id="pack-essence" className="mt-5 border border-emerald-200 bg-emerald-50/50 rounded-xl p-4" data-testid="pack-essence" data-energy={pack.energy}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2"><PackagePlus size={18} className="text-emerald-600" /> {t(pack.energy === 'diesel' ? 'packEss.titleDiesel' : 'packEss.title')}</h3>
        <span className="text-xs font-bold text-white bg-emerald-600 rounded-full px-2.5 py-0.5" data-testid="pack-essence-percent">-{pack.percent.toFixed(0)} % {t('packEss.onAdditive')}</span>
      </div>
      <p className="text-xs text-gray-600 mt-1">{t(pack.energy === 'diesel' ? 'packEss.descDiesel' : 'packEss.desc', { p: pack.percent.toFixed(0) })}</p>
      <div className="mt-3 flex items-center gap-3">
        <img src={pack.oil.image} alt="" className="w-14 h-14 object-contain bg-white rounded-lg border border-gray-200" />
        <Plus size={16} className="text-gray-400 shrink-0" />
        <Link to={`/produit/${pack.additive.slug || pack.additive.id}`} className="shrink-0" data-testid="pack-essence-additive-link"><img src={pack.additive.image} alt="" className="w-14 h-14 object-contain bg-white rounded-lg border border-gray-200" /></Link>
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-medium text-gray-900 line-clamp-2" data-testid="pack-essence-additive-name">{prodName(pack.additive, lang)}</p>
          <p className="text-xs text-gray-500"><span className="line-through">{eur(pack.additive.price)}</span> <span className="font-semibold text-emerald-700">{eur(pack.additive_discounted)}</span></p>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm" data-testid="pack-essence-total">{t('packEss.total')} <span className="text-lg font-bold text-gray-900">{eur(pack.pack_price)}</span> <span className="text-xs text-gray-500 line-through">{eur(pack.full_price)}</span> <span className="text-xs font-semibold text-emerald-700">(-{eur(pack.discount)})</span></p>
        <Button onClick={add} disabled={busy || done} className="bg-emerald-600 hover:bg-emerald-700 text-white h-10" data-testid="pack-essence-add">
          {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : done ? <CheckCircle2 size={16} className="mr-2" /> : <PackagePlus size={16} className="mr-2" />}
          {done ? t('packEss.done') : t('packEss.add')}
        </Button>
      </div>
      <p className="text-[11px] text-gray-500 mt-2">{t('packEss.code', { c: pack.code })}</p>
    </section>
  );
};

export default PackEssenceCard;
